import { z } from 'zod'
import { activeStatusSchema } from './active'

/**
 * Reports contracts (Screen 4). Pinned to the backend's three GET endpoints —
 * /api/reports/fees, /api/reports/value, /api/reports/top-products — being built
 * to these exact wire formats in parallel. All money and percentages arrive as
 * decimal STRINGS so no float drift creeps in over the wire; the UI parses them
 * to integer cents for any math (see report-format.ts).
 *
 * Fee-line status reuses the Active Dropships status enum so the same badge
 * helper renders it. The contract only lists SUBMITTED|TRACKING_RECEIVED|
 * SHOPIFY_UPDATED|ERROR|OVERDUE; CANCELLED is a superset value that simply never
 * appears in a fee report (cancelled orders carry no fee).
 */

// ---- Panel 1: GET /api/reports/fees ----

export const feeLineSchema = z.object({
  order_number: z.string(),
  customer_name: z.string(),
  items_sent: z.number(),
  submitted_at: z.string(),
  status: activeStatusSchema,
  wholesale_value: z.string(),
  dropship_fee: z.string(),
})
export type FeeLine = z.infer<typeof feeLineSchema>

export const feesReportSchema = z.object({
  start: z.string(),
  end: z.string(),
  order_count: z.number(),
  total_fees: z.string(),
  lines: z.array(feeLineSchema),
})
export type FeesReport = z.infer<typeof feesReportSchema>

// ---- Panel 2: GET /api/reports/value ----

export const valuePeriodSchema = z.object({
  start: z.string(),
  end: z.string(),
  retail_value: z.string(),
  wholesale_value: z.string(),
  total_fees: z.string(),
  gross_margin_pct: z.string().nullable(),
  order_count: z.number(),
})
export type ValuePeriod = z.infer<typeof valuePeriodSchema>

export const valueReportSchema = z.object({
  current: valuePeriodSchema,
  previous: valuePeriodSchema.nullable(),
})
export type ValueReport = z.infer<typeof valueReportSchema>

// ---- Panel 3: GET /api/reports/top-products ----

export const stockSignalSchema = z.enum(['RED', 'YELLOW', 'GREEN'])
export type StockSignal = z.infer<typeof stockSignalSchema>

export const topProductRowSchema = z.object({
  sku: z.string(),
  title: z.string(),
  units: z.number(),
  orders: z.number(),
  retail_value: z.string(),
  wholesale_value: z.string(),
  signal: stockSignalSchema,
})
export type TopProductRow = z.infer<typeof topProductRowSchema>

export const topProductsReportSchema = z.object({
  start: z.string(),
  end: z.string(),
  rows: z.array(topProductRowSchema),
})
export type TopProductsReport = z.infer<typeof topProductsReportSchema>
